// This controller is attached to each tab inside the messages page and the Message tab inside the pair/circle page

// NOTE: the data-alert-id will be `circle-message` or `target-message`
//        this prevents the case where circle and target id are both 12
//        we don't want `data-alert-id="message-12-3"` & `data-alert-id="message-12-3"` on the DOM

// Use Set(no duplicates) instead of Array(allows duplicates)
// Example: if someone has 3 messages inside the circle, the alert will show "circle-message-12-3" 3 times

// Module Level (Shared across all instances):
// ┌─────────────────────────┐
// │ removedDots Set         │
// │ { 'c-message-12-3',     │
// │   't-message-37-3' }    │
// └─────────────────────────┘
//          ↑       ↑       ↑
//          │       │       │
//     ┌────┴───┬───┴────┬──┴─────┐
//     │ Tab 1  │ Tab 2  │Dropdown│
//     │Instance│Instance│Instance│
//     └────────┴────────┴────────┘

import { Controller } from "@hotwired/stimulus";

const removedDots = new Set(); // this will reset on fresh page load

export default class extends Controller {
  static values = {
    activitiesTargetId: Number,
    activitiesCircleId: Number,
    programId: Number,
    adminLoggedInAsUser: Boolean,
    programDropdown: Boolean
  }

  connect() {
    if (this.adminLoggedInAsUserValue == false) {
      // On page load, the first tab will be active
      requestAnimationFrame(() => {
        // Connects once since the programDropdown value is only on the dropdown element
        if (this.hasProgramDropdownValue) {
          this.observeDropdownOpenState();
        }
      })

      const observerCallback = (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isVisible) {
            this.removeAlertDot();

            // Stop observing the element after the first intersection
            observer.unobserve(entry.target);
          }
        });
      };

      const observer = new IntersectionObserver(observerCallback, {trackVisibility: true, delay: 100});
      observer.observe(this.element);
    }
  }

  observeDropdownOpenState() {
    const button = document.querySelector('.program-selector-dropdown');
    if (!button) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-headlessui-state') {
          const isOpen = button.getAttribute('data-headlessui-state') === 'open';

          if (isOpen) {
            this.removeTrackedDotsFromDropdown();
          }
        }
      });
    });

    observer.observe(button, { attributes: true });
  }

  removeTrackedDotsFromDropdown() {
    // Loop through all tracked dots and remove them from the dropdown
    removedDots.forEach(dotId => {
      const badges = document.querySelectorAll(`.program-selector-dropdown [data-alert-id="${dotId}"]`);
      badges.forEach(element => element.remove());
    });

    // NOTE: PrimeVue uses the original template/HTML (which has dots) so this function
    //       makes sure the dots are gone when the dropdown open/closes
  }

  removeAlertDot() {
    let program_id = this.programIdValue;
    let target_id = null;
    let circle_id = null;
    let badges_to_remove = null;
    let dotId = null;

    if (this.hasActivitiesCircleIdValue && this.activitiesCircleIdValue > 0) {
      circle_id = this.activitiesCircleIdValue;
      dotId = `circle-message-${circle_id}-${program_id}`;
      badges_to_remove = document.querySelectorAll(`[data-alert-id="${dotId}"]`);
    } else if (this.hasActivitiesTargetIdValue) {
      target_id = this.activitiesTargetIdValue;
      dotId = `target-message-${target_id}-${program_id}`;
      badges_to_remove = document.querySelectorAll(`[data-alert-id="${dotId}"]`);
    }

    // Inside the circle/pair page "Messages" subtitle
    if (target_id || circle_id) {
      if (document.querySelector('#tab-subtitle-panel-messages .total')) {
        document.querySelector('#tab-subtitle-panel-messages .total').innerHTML = '0'
      }
    }

    // 1. Click Tab 1 → Dot removed from all the tabs on the DOM
    // 2. Dot in dropdown not removed yet (since it's not rendered)
    // 3. dotId is tracked in removedDots
    // 4. Open dropdown → Dot removed inside dropdon
    if (!badges_to_remove || badges_to_remove.length === 0) {
      // Even if no badges found now, track it for when dropdown opens
      if (dotId) {
        removedDots.add(dotId);
      }
      return;
    }

    if (dotId) {
      removedDots.add(dotId);
    }

    // Build data hash for the request
    const data_hash = {
      program_id: program_id.toString()
    };

    if (circle_id) {
      data_hash.circle_id = circle_id;
    } else if (target_id) {
      data_hash.target_id = target_id;
    }

    fetch('/my/messages/read', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify(data_hash)
    })
    .then(response => {
      if (response.ok) {
        badges_to_remove.forEach(element => {
          element.remove();
        });
        const totalElement = document.querySelector('.tab-messages .total');
        if (totalElement) {
          totalElement.innerHTML = '0';
        }
      }
    })
    .catch(error => {
      console.error("Error marking messages as read:", error);
    });
  }
}